import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { Flame, TrendingDown, Star, Camera } from "lucide-react";
import { useSettings } from "../../hooks/useSettings";
import { DownloadCTA } from "../ui/DownloadCTA";
import { CalorieDial } from "../visuals/CalorieDial";
import { Spark } from "../ui/decor";

// Real faces reused as the social-proof avatar stack.
const AVATARS = [
  "/photos/hero-person.jpg",
  "/photos/persona-fitness.jpg",
  "/photos/persona-glp1.jpg",
  "/photos/persona-quit.jpg",
];

export function Hero() {
  const s = useSettings();
  const reduce = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Gate the scroll-driven parallax/fade to desktop so the hero visual stays
  // put and fully visible on mobile.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = () => setIsDesktop(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Layered parallax — each element drifts at its own speed for depth.
  const p = (to: number) => useTransform(scrollYProgress, [0, 1], [0, (reduce || !isDesktop) ? 0 : to]);
  const yCopy = p(48);
  const yPhone = p(120);
  const yPhoto = p(-40);
  const yEarned = p(200);
  const yStreak = p(70);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative isolate overflow-hidden pb-24 pt-36 lg:pb-28 lg:pt-44"
    >
      {/* Warm, playful canvas */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Big flat consumer shapes instead of blurred SaaS glows */}
        <div className="absolute -right-28 -top-32 h-[30rem] w-[30rem] rounded-full bg-sun/30" />
        <div className="absolute -left-36 bottom-[-6rem] h-96 w-96 rounded-full bg-teal-mist/80" />
        <div className="absolute right-[16%] bottom-24 h-24 w-24 rounded-full bg-coral-pale" />
      </div>

      {/* Floating sparkles */}
      <Spark className="absolute left-[6%] top-36 hidden animate-wiggle lg:block" />
      <Spark
        fill="#8B5CF6"
        className="absolute right-[8%] top-64 hidden h-6 w-6 animate-wiggle lg:block"
      />

      <div className="container-x grid items-center gap-16 lg:grid-cols-[1.04fr_0.96fr]">
        {/* Copy */}
        <motion.div style={{ y: yCopy }} className="relative z-10 text-center lg:text-left">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="display text-[3rem] leading-[1.02] text-navy sm:text-6xl lg:text-[4.4rem] xl:text-[5rem]"
          >
            {s.hero_headline.split(".").map((part, i) =>
              part.trim() ? (
                <span key={i} className="block">
                  {i === 1 ? (
                    <span className="marker">{part.trim()}.</span>
                  ) : (
                    <span>{part.trim()}.</span>
                  )}
                </span>
              ) : null
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-charcoal/80 sm:text-lg lg:mx-0"
          >
            {s.hero_subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col items-center gap-6 lg:items-start"
          >
            <DownloadCTA source="hero" className="!items-center lg:!items-start" />

            {/* Social proof */}
            <div className="flex flex-col items-center gap-2.5 sm:flex-row sm:gap-4 lg:items-center">
              <div className="flex -space-x-2.5">
                {AVATARS.map((src) => (
                  <span
                    key={src}
                    className="inline-block h-10 w-10 overflow-hidden rounded-full border-2 border-navy shadow-pop-xs"
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </span>
                ))}
              </div>
              <div className="text-center sm:text-left">
                <span className="flex items-center justify-center gap-1 text-amber-400 sm:justify-start">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                  <span className="num ml-1 text-sm font-semibold text-navy">4.9</span>
                </span>
                <p className="text-xs text-charcoal/60">
                  Loved by <span className="font-semibold text-navy">12,000+</span> members banking calories
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Product-forward collage: real photo + the app, stickers everywhere */}
        <div className="relative mx-auto flex w-full max-w-[27rem] justify-center">
          <div className="relative">
            {/* Flat sunny disc anchoring the collage */}
            <div
              aria-hidden
              className="absolute -inset-8 -z-10 rounded-full bg-sun/35"
            />

            {/* Real celebration photo, tucked behind the phone */}
            <motion.div
              style={{ y: yPhoto }}
              initial={{ opacity: 0, x: -24, rotate: -10 }}
              animate={{ opacity: 1, x: 0, rotate: -6 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-8 -top-12 z-0 w-44 sm:-left-24 sm:w-56"
            >
              <div className="relative overflow-hidden rounded-[1.75rem] border-[3px] border-navy shadow-pop">
                <img
                  src="/photos/hero-cheer.jpg"
                  alt="A FitBuddy member celebrating her weekly calorie payout"
                  className="aspect-[3/4] h-full w-full object-cover object-top"
                />
                <div className="chip absolute left-2 top-2 -rotate-3 !bg-white">
                  <span className="num font-bold text-teal">−6.2 kg</span>
                </div>
              </div>
            </motion.div>

            {/* The phone */}
            <motion.div
              style={{ y: yPhone, opacity: isDesktop ? fade : 1 }}
              initial={{ opacity: 0, y: 40, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: 3 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
            >
              <PhoneScreen />
            </motion.div>

            {/* Floating "earned" sticker card */}
            <motion.div
              style={{ y: yEarned }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="absolute -left-4 top-44 z-20 sm:-left-14 sm:top-52"
            >
              <motion.div
                animate={reduce ? undefined : { y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
                className="flex -rotate-2 items-center gap-3 rounded-2xl border-[3px] border-navy bg-white px-4 py-3 shadow-pop-sm"
              >
                <span className="grid h-10 w-10 place-items-center rounded-full border-2 border-navy bg-teal-mist text-teal">
                  <TrendingDown className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold leading-tight text-navy">
                  You just earned
                  <span className="num ml-1 font-bold text-teal">+2,500</span>
                  <span className="block text-xs font-normal text-charcoal/60">
                    calories to spend
                  </span>
                </span>
              </motion.div>
            </motion.div>

            {/* Floating streak sticker card */}
            <motion.div
              style={{ y: yStreak }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="absolute -bottom-6 -right-3 z-20 sm:-right-10"
            >
              <motion.div
                animate={reduce ? undefined : { y: [0, 9, 0] }}
                transition={{ duration: 6, repeat: Infinity, delay: 0.5 }}
                className="flex rotate-2 items-center gap-3 rounded-2xl border-[3px] border-navy bg-sun px-4 py-3 shadow-pop-sm"
              >
                <span className="grid h-10 w-10 place-items-center rounded-full border-2 border-navy bg-white text-coral">
                  <Flame className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold leading-tight text-navy">
                  <span className="num text-lg font-bold text-navy">14</span>-day streak
                  <span className="block text-xs font-normal text-navy/60">
                    keep the bank open
                  </span>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * A polished, static showcase of the app's home screen, styled as the real
 * product should look: the same warm, chunky "sticker pop" language as the
 * site — light screen, ink outlines, sun accents, the bank dial front and
 * centre. This mock is the design reference for the app itself.
 */
function PhoneScreen() {
  return (
    <div className="relative w-[16.5rem] sm:w-[18rem]">
      {/* Device frame — chunky ink outline, hard sticker shadow */}
      <div className="relative rounded-[2.9rem] border-[3px] border-navy bg-navy p-2.5 shadow-pop">
        <div className="absolute left-1/2 top-2.5 z-20 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-navy" />
        <div className="relative overflow-hidden rounded-[2.35rem] bg-gradient-to-b from-white to-cream px-4 pb-5 pt-9">
          {/* App bar */}
          <div className="mb-2 flex items-center justify-between">
            <span className="font-display text-sm font-extrabold text-navy">
              Fit<span className="text-teal">Buddy</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border-2 border-navy bg-sun px-2.5 py-0.5 text-[11px] font-extrabold text-navy">
              <Flame className="h-3 w-3 text-coral" />
              <span className="num">14</span>-day
            </span>
          </div>

          <p className="mb-2 text-xs font-medium text-charcoal/60">Good morning, Sarah 👋</p>

          {/* Bank card — the signature dial on its own chunky widget */}
          <div className="rounded-3xl border-2 border-navy bg-white p-2 shadow-pop-xs">
            <div className="flex justify-center">
              <CalorieDial value={12480} max={15000} size={158} label="CAL BANKED" theme="light" />
            </div>
            {/* Weight readout */}
            <div className="mb-1.5 flex items-center justify-center gap-2 text-sm">
              <span className="text-charcoal/55">Weight</span>
              <span className="num font-bold text-navy">78.4 kg</span>
              <span className="num rounded-full bg-teal-mist px-1.5 text-xs font-bold text-teal">▼ 0.3</span>
            </div>
          </div>

          {/* Receipt — a sunny payout slip */}
          <div className="mt-3 rounded-2xl border-2 border-navy bg-sun-pale p-3">
            {[
              ["All-time low", "+1,600"],
              ["3-day average", "+700"],
              ["Showing up", "+200"],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between py-0.5 text-xs">
                <span className="font-medium text-charcoal/70">{l}</span>
                <span className="num font-bold text-teal">{v}</span>
              </div>
            ))}
            <div className="mt-2 flex justify-between border-t-2 border-dashed border-navy/25 pt-2 text-xs">
              <span className="font-display font-extrabold text-navy">Earned today</span>
              <span className="num font-bold text-violet">+2,500</span>
            </div>
          </div>

          {/* Log button */}
          <div className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy bg-teal py-2.5 font-display text-sm font-extrabold text-white shadow-pop-xs">
            <Camera className="h-4 w-4" /> Log weigh-in
          </div>
        </div>
      </div>
    </div>
  );
}
